import type { AbsenceStatus, BadgeTone } from '../absences/types';

export type OvertimeInputMode = 'DAYS' | 'HOURS';

export interface OvertimeBalance {
  dayMinutes: number;
  accruedMinutes: number;
  pendingMinutes: number;
  redeemedMinutes: number;
  /** Minutos gastados en saldar déficits de jornada (compensaciones activas). */
  deficitCoveredMinutes: number;
  availableMinutes: number;
  availableDays: number;
  availableRemainderMinutes: number;
}

export interface OvertimeRequestSummary {
  id: string;
  userId: string;
  minutesRequested: number;
  dayMinutesAtRequest: number;
  daysGrantedSnapshot: string; // Decimal serializado por Prisma
  inputMode: OvertimeInputMode;
  comment: string | null;
  status: AbsenceStatus;
  approverUserId: string | null;
  decidedAt: string | null;
  decisionNotes: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; fullName: string; email: string };
}

export const STATUS_TONE_OVERTIME: Record<AbsenceStatus, BadgeTone> = {
  PENDING: 'amber',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'neutral',
};

export const STATUS_LABEL_OVERTIME: Record<AbsenceStatus, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

export function formatHm(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return '—';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Aprobación de horas a compensar por jornada (post-fichaje OUT)
// ---------------------------------------------------------------------------

/** Jornada con horas a compensar pendientes de decidir por el empleado. */
export interface PendingOvertimeWorkday {
  workdayId: string;
  date: string;
  overtimeMinutes: number;
  workedMinutes: number;
  expectedMinutes: number;
}

/** Jornada con horas a compensar aprobadas — entrada del histórico con notas. */
export interface ApprovedOvertimeWorkday extends PendingOvertimeWorkday {
  notes: string | null;
  decidedAt: string | null;
}

/**
 * Entrada unificada del histórico: workday aprobado o ajuste manual de HR.
 * `workedMinutes` y `expectedMinutes` solo aplican a jornadas; en ajustes
 * llegan null. `createdByName` es el HR que añadió el ajuste (null para
 * workdays propios del empleado).
 */
export interface OvertimeHistoryEntry {
  kind: 'WORKDAY' | 'MANUAL_ADJUSTMENT';
  id: string;
  date: string;
  overtimeMinutes: number;
  workedMinutes: number | null;
  expectedMinutes: number | null;
  notes: string | null;
  decidedAt: string | null;
  createdByName: string | null;
}

/** "1 d 2h" / "3 d" / "5h 30m" / "0h" según el saldo. */
export function formatDaysAndHours(
  totalMinutes: number,
  dayMinutes: number,
): string {
  if (totalMinutes <= 0) return '0h';
  if (dayMinutes <= 0) return formatHm(totalMinutes);
  const days = Math.floor(totalMinutes / dayMinutes);
  const rest = totalMinutes - days * dayMinutes;
  if (days === 0) return formatHm(rest);
  if (rest === 0) return `${days} d`;
  return `${days} d ${formatHm(rest)}`;
}
